import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";
import SEOHead from "@/components/feature/SEOHead";

const sections = [
  {
    id: "overview",
    title: "Overview",
    content: `This Privacy Policy explains how The Wraith Chronicles website ("we," "us," or "our") collects, uses, and protects any information you provide when you visit this site. We take your privacy seriously and are committed to being transparent about our practices.`,
  },
  {
    id: "information-collected",
    title: "Information We Collect",
    items: [
      {
        label: "Contact & Message Forms",
        text: `When you submit a message through our "I Want More" or contact forms, we collect your name, email address (if provided), and the content of your message. This information is used solely to respond to your inquiry or to notify you of future updates if you opted in.`,
      },
      {
        label: "Usage Data",
        text: `We may collect basic, anonymized data about how visitors interact with the site — such as pages visited and time spent — through standard web analytics. This data contains no personally identifiable information.`,
      },
      {
        label: "Cookies",
        text: `This site may use minimal session cookies to maintain basic functionality. We do not use tracking cookies, advertising cookies, or third-party analytics cookies that identify you personally.`,
      },
    ],
  },
  {
    id: "how-we-use",
    title: "How We Use Your Information",
    items: [
      { label: "To respond to messages", text: "If you contact us, we use your information to reply." },
      { label: "To send updates", text: "If you provided your email and requested updates about future books or content, we may reach out. You can opt out at any time." },
      { label: "To improve the site", text: "Anonymized usage data helps us understand what content resonates with readers." },
    ],
  },
  {
    id: "data-sharing",
    title: "Data Sharing & Third Parties",
    content: `We do not sell, trade, or rent your personal information to anyone. Ever. We may use trusted third-party services (such as form submission processors) that handle data on our behalf under strict confidentiality. These services are not permitted to use your data for their own purposes.`,
  },
  {
    id: "data-retention",
    title: "Data Retention",
    content: `We retain form submissions only as long as necessary to respond to your message or fulfill the purpose for which it was submitted. You may request deletion of your data at any time by contacting us.`,
  },
  {
    id: "your-rights",
    title: "Your Rights",
    items: [
      { label: "Access", text: "You can request a copy of any personal data we hold about you." },
      { label: "Correction", text: "You can ask us to correct inaccurate information." },
      { label: "Deletion", text: "You can request that we delete your personal data." },
      { label: "Opt-out", text: "You can unsubscribe from any communications at any time." },
    ],
  },
  {
    id: "childrens-privacy",
    title: "Children's Privacy",
    content: `This site is not directed at children under the age of 13. We do not knowingly collect personal information from children. If you believe a child has submitted information to us, please contact us and we will promptly delete it.`,
  },
  {
    id: "changes",
    title: "Changes to This Policy",
    content: `We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated effective date. Continued use of the site after changes constitutes acceptance of the revised policy.`,
  },
  {
    id: "contact",
    title: "Contact",
    content: `If you have any questions about this Privacy Policy or how your data is handled, you can reach us through the contact form on this site.`,
  },
];

export default function PrivacyPolicyPage() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-[#0A0A0A] min-h-screen w-full">
      <SEOHead
        title="Privacy Policy | The Wraith Chronicles"
        description="Privacy Policy for The Wraith Chronicles website. Learn how we collect, use, and protect your information."
        noIndex
      />
      <Navbar />

      {/* Hero */}
      <div className="relative w-full pt-36 pb-16 px-6 overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              "repeating-linear-gradient(0deg, transparent, transparent 28px, #C8922A 28px, #C8922A 29px)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A] via-transparent to-[#0A0A0A]" />

        <div className="relative max-w-2xl mx-auto text-center">
          <div className="flex items-center justify-center gap-4 mb-10">
            <div className="w-12 h-px bg-[#C8922A]/30" />
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-shield-line text-[#C8922A]/50 text-base" />
            </div>
            <div className="w-12 h-px bg-[#C8922A]/30" />
          </div>

          <h1 className="font-['Special_Elite'] text-white text-5xl md:text-6xl tracking-[0.06em] uppercase mb-6 leading-tight">
            Privacy<br />
            <span className="text-[#C8922A]">Policy</span>
          </h1>

          <p className="font-['Share_Tech_Mono'] text-[#6A6A6A] text-xs tracking-[0.25em] uppercase">
            Effective Date: April 2026
          </p>
        </div>
      </div>

      {/* Divider */}
      <div className="max-w-2xl mx-auto px-6">
        <div className="flex items-center gap-4">
          <div className="flex-1 h-px bg-[#1E1E1E]" />
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-lock-line text-[#C8922A]/30 text-xs" />
          </div>
          <div className="flex-1 h-px bg-[#1E1E1E]" />
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-6 py-16 space-y-12">
        {sections.map((section) => (
          <div key={section.id} id={section.id}>
            <h4 className="font-['Share_Tech_Mono'] text-[#C8922A] text-xs tracking-[0.3em] uppercase mb-5 flex items-center gap-3">
              <a href={`#${section.id}`} className="hover:text-[#D9A030] transition-colors">
                {section.title}
              </a>
              <span className="flex-1 h-px bg-[#1A1A1A]" />
            </h4>

            {section.content && (
              <p className="font-['Rajdhani'] text-[#888] text-base leading-relaxed">
                {section.content}
              </p>
            )}

            {section.items && (
              <ul className="space-y-5">
                {section.items.map((item) => (
                  <li key={item.label} className="border-l-2 border-[#1E1E1E] pl-5">
                    <div className="font-['Share_Tech_Mono'] text-[#555] text-xs tracking-[0.2em] uppercase mb-1">
                      {item.label}
                    </div>
                    <p className="font-['Rajdhani'] text-[#888] text-base leading-relaxed">
                      {item.text}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>

      {/* Cross-link to Terms of Use */}
      <div className="max-w-2xl mx-auto px-6 pb-8">
        <div className="border border-[#1A1A1A] bg-[#0D0D0D] px-6 py-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <div className="font-['Share_Tech_Mono'] text-[#444] text-xs tracking-[0.25em] uppercase mb-1">
              Also Read
            </div>
            <p className="font-['Rajdhani'] text-[#666] text-sm">
              Our Terms of Use explain the rules governing your use of this site.
            </p>
          </div>
          <button
            onClick={() => navigate("/terms-of-use")}
            className="flex items-center gap-2 border border-[#C8922A]/30 hover:border-[#C8922A]/60 text-[#C8922A]/70 hover:text-[#C8922A] text-xs tracking-[0.15em] uppercase font-['Share_Tech_Mono'] px-5 py-3 transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-file-text-line text-sm" />
            Terms of Use
          </button>
        </div>
      </div>

      {/* Back link */}
      <div className="max-w-2xl mx-auto px-6 pb-20 text-center">
        <button
          onClick={() => navigate("/")}
          className="font-['Share_Tech_Mono'] text-[#333] hover:text-[#C8922A]/60 text-xs tracking-[0.25em] uppercase transition-colors duration-300 cursor-pointer flex items-center gap-2 mx-auto"
        >
          <i className="ri-arrow-left-line text-xs" />
          Back to Home
        </button>
      </div>

      <Footer />
    </div>
  );
}
